/**
 * Hook for accessing the current user's consent record.
 *
 * Uses TanStack Query with a short stale time so simulation components
 * always see up-to-date voice recording consent status.
 *
 * @module useConsentStatus
 */

import { useQuery } from "@tanstack/react-query";
import { getMyConsent, type ConsentRecord } from "@/lib/consentApi";
import { CURRENT_POLICY_VERSION } from "@/lib/consentConfig";

export const CONSENT_QUERY_KEY = ["consentStatus"] as const;

export interface ConsentStatus {
  consentRecord: ConsentRecord | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  hasVoiceConsent: boolean;
  needsConsent: boolean;
  refetch: () => void;
}

export function useConsentStatus(): ConsentStatus {
  const { data, isLoading, isFetching, isError, refetch } = useQuery<ConsentRecord | null>({
    queryKey: CONSENT_QUERY_KEY,
    queryFn: getMyConsent,
    staleTime: 30_000,
    retry: 2,
  });

  const consentRecord = data ?? null;
  const hasVoiceConsent = consentRecord?.voice_recording_consent === true;
  // Only signal needsConsent when we have stable (non-fetching) data.
  // Suppressing during background refetches (e.g. after settings save) prevents
  // ConsentGate from redirecting while the cache is momentarily empty.
  const needsConsent =
    !isFetching &&
    (!consentRecord || consentRecord.consent_policy_version < CURRENT_POLICY_VERSION);

  return {
    consentRecord,
    isLoading,
    isFetching,
    isError,
    hasVoiceConsent,
    needsConsent,
    refetch,
  };
}
